import { allowedRecordCategories } from "../../config/search-permissions";
import moment from 'moment';

export const normalize = (value) => String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();

const distance = (a, b) => {
  let row = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const next = [i];
    for (let j = 1; j <= b.length; j++) next[j] = Math.min(next[j - 1] + 1, row[j] + 1, row[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    row = next;
  }
  return row[b.length];
};
export const featureScore = (feature, query) => {
  const q = normalize(query), name = normalize(feature.name), words = normalize(feature.keywords).split(' ');
  if (!q) return 0;
  if (name === q) return 100;
  if ((feature.synonyms || []).some(s => normalize(s) === q)) return 95;
  if (name.startsWith(q)) return 90;
  if (name.includes(q)) return 80;
  const synonyms = normalize(feature.keywords);
  if (synonyms.includes(q)) return 70;
  const candidates = [...name.split(' '), ...words];
  const minorTypo = (word, token) => {
    if (Math.abs(word.length - token.length) > 1) return false;
    if (distance(word, token) <= 1) return true;
    // Adjacent transpositions are common when typing quickly.
    const differences = [...token].map((letter, index) => letter !== word[index] ? index : -1).filter(index => index >= 0);
    return differences.length === 2 && differences[1] === differences[0] + 1 && token[differences[0]] === word[differences[1]] && token[differences[1]] === word[differences[0]];
  };
  if (q.length >= 4 && q.split(' ').every(token => candidates.some(word => word === token || (token.length >= 4 && minorTypo(word, token))))) return 40;
  if (normalize(feature.description).includes(q)) return 30;
  return 0;
};
export const searchFeatures = (features, query, visits = []) => features.map(feature => ({
  ...feature, score: featureScore(feature, query), frequency: visits.filter(v => v.feature === feature.id).length,
})).filter(f => f.score > 0).sort((a, b) => b.score - a.score || b.frequency - a.frequency || a.name.localeCompare(b.name));

export const historyKey = (account) => {
  const accountId = account?.details?.host_id || account?.details?.account_id || account?.user?.account_id || account?.account_id;
  const userId = account?.user?.user_id;
  return accountId && userId ? `rbr-search:v1:${accountId}:${userId}` : null;
};
export const readHistory = (key) => {
  try {
    const data = JSON.parse(localStorage.getItem(key));
    if (!key || !data || Date.now() - data.saved > 30 * 86400000) return { searches: [], visits: [] };
    return {
      searches: (Array.isArray(data.searches) ? data.searches : []).filter(s => typeof s === 'string' && s.length <= 120).slice(0, 10),
      visits: (Array.isArray(data.visits) ? data.visits : []).filter(v => v && !v.record && typeof v.feature === 'string').slice(0, 10),
    };
  } catch (_) { return { searches: [], visits: [] }; }
};
export const writeHistory = (key, data) => {
  try { if (key) localStorage.setItem(key, JSON.stringify({ ...data, saved: Date.now() })); } catch (_) { /* Storage can be disabled. */ }
};
export const addSearch = (searches, query) => [query.trim(), ...searches.filter(s => normalize(s) !== normalize(query))].filter(Boolean).slice(0, 10);

const crmSlugs = { owner: 'owners', guest: 'guests', leads: 'leads', housekeeper: 'housekeepers', maintenance: 'maintenance', vendor: 'vendors' };
export const recordResult = (item, category, account, permissions) => {
  if (!allowedRecordCategories(permissions, account).includes(category)) return null;
  let route, description, icon;
  if (category === 'properties' || category === 'home-guides') {
    route = category === 'properties' ? `/unit_detail/${item.id}` : `/properties/${item.id}`;
    description = [item.pms_id, item.address].filter(Boolean).join(' · '); icon = category === 'properties' ? 'home' : 'book-open';
  } else if (category === 'tasks') {
    route = permissions.canView("operations.work_orders")
      ? `${permissions.canView("operations.tasks") ? "/operations/tasks" : "/work-orders"}/${item.id}`
      : `/tasks/${item.id}`;
    description = [item.status, item.property].filter(Boolean).join(' · '); icon = 'check-square';
  } else if (category === 'bookings') {
    route = `/guests?booking_id=${item.id}`;
    description = [item.property, item.reservation_id, `${String(item.check_in || '').slice(0, 10)} – ${String(item.check_out || '').slice(0, 10)}`].filter(Boolean).join(' · '); icon = 'calendar';
  } else {
    const crm = !item.legacy && permissions.canView('crm.contacts') && account.flags?.some(flag => flag.name === 'crm');
    route = crm ? `/crm/${crmSlugs[item.contact_type] || 'other'}/${item.id}/overview`
      : item.contact_id && permissions.canView('messaging.contacts') ? `/contacts?contact_id=${item.contact_id}`
      : item.booking_id && permissions.canView('messaging.guests') ? `/guests?booking_id=${item.booking_id}` : null;
    const stay = item.check_in && item.check_out
      ? `${moment.utc(String(item.check_in).slice(0, 10)).format('MMM D, YYYY')} - ${moment.utc(String(item.check_out).slice(0, 10)).format('MMM D, YYYY')}` : '';
    description = [item.property, stay].filter(Boolean).join(' · ')
      || item.contact_type;
    if (category !== 'guests') description = [item.contact_type, item.property].filter(Boolean).join(' · ');
    icon = 'user';
  }
  return route ? { id: `${category}:${item.id}`, recordId: item.id, name: item.name, description, icon, route, category, record: true } : null;
};

// Recently viewed contains feature destinations only, never individual records.
export const recentVisit = (location, features) => {
  const feature = features.find(f => f.route === location.pathname + (location.search || '')) || features.find(f => f.route === location.pathname);
  return feature ? { feature: feature.id } : null;
};
